import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/contexts/LocaleContext';
import { optimizedImageUrl } from '@/lib/storage';
import { useHomeHosts } from '@/hooks/useHomeHosts';
import { useCategories } from '@/hooks/useCategories';
import { getCategoryIcon, getCategoryColorClass, getBrandColorByIndex } from '@/lib/categoryStyles';
import { CarouselScroller } from './CarouselScroller';

export function HostsCarousel() {
  const { t, locale } = useLocale();
  const isArabic = locale === 'ar';
  const { data: hosts = [], isLoading } = useHomeHosts(12);
  const { data: categories = [] } = useCategories();

  if (isLoading) return null;
  if (hosts.length === 0) return null;

  return (
    <section id="meet-hosts" className="py-6 md:py-8 scroll-mt-24">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-3 md:mb-4">
          <div>
            <h2 className="text-lg md:text-2xl font-bold">
              {isArabic ? 'منو ياينه هالحزة؟' : 'Meet Our Hosts'}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {isArabic ? 'تعرّف على مزوّدي الخدمة المميزين في باليت' : 'Discover our featured Palette hosts'}
            </p>
          </div>
          <Button variant="ghost" asChild>
            <Link to="/hosts">{t('viewAll')} →</Link>
          </Button>
        </div>

        <CarouselScroller arrowsTop="40%" resetKey={hosts.length}>
          <div className="grid grid-flow-col auto-cols-[48%] sm:auto-cols-[32%] md:auto-cols-[calc((100%-5rem)/5)] gap-3 md:gap-4">
            {hosts.map((h, i) => {
              const cat = categories.find((c) => c.id === h.category_id);
              const categoryName = cat
                ? isArabic
                  ? cat.name_ar
                  : (cat.name_en || cat.name_ar)
                : null;
              const CatIcon = getCategoryIcon(cat?.icon || null);
              const catColor = getCategoryColorClass(cat?.color || null);
              const displayName = h.display_name || h.full_name || (isArabic ? 'مضيف' : 'Host');
              return (
                <Link
                  key={h.user_id}
                  to={`/hosts/${h.user_id}`}
                  className="animate-fade-in snap-start group block"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className={`aspect-[4/5] overflow-hidden relative rounded-2xl flex items-center justify-center p-4 ${getBrandColorByIndex(i)}`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/25 to-transparent pointer-events-none" />
                    {h.avatar_url ? (
                      <img
                        src={optimizedImageUrl(h.avatar_url, { width: 500, quality: 85 })}
                        alt={displayName}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover rounded-2xl border-4 border-white/60 shadow-elevated transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full rounded-2xl bg-white/30 border-4 border-white/60 flex items-center justify-center text-white text-5xl font-extrabold drop-shadow">
                        {displayName.charAt(0)}
                      </div>
                    )}
                    {h.avg_rating !== null && (
                      <div className="absolute bottom-2 start-2 z-10 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2 py-0.5 text-[11px] md:text-xs font-bold shadow">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {h.avg_rating.toFixed(1)}
                      </div>
                    )}
                  </div>

                  <div className="pt-2 px-0.5">
                    <h3 className="text-sm md:text-base font-bold line-clamp-1 leading-tight">
                      {displayName}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {categoryName && (
                        <span className={`inline-flex items-center gap-1 text-[10px] md:text-xs font-medium text-white px-2 py-0.5 rounded-full ${catColor}`}>
                          <CatIcon className="w-3 h-3" />
                          {categoryName}
                        </span>
                      )}
                    </div>
                    {h.bookings_count > 0 && (
                      <p className="text-[11px] md:text-xs text-muted-foreground mt-1 line-clamp-1">
                        {h.bookings_count} {isArabic ? 'حجز' : 'bookings'}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </CarouselScroller>
      </div>
    </section>
  );
}

